import Route from '@routes/utils'
import { getRouteByPath } from '@routes/utils/getRouteBy'
import { isMailtoUrl } from '@routes/utils/isMailtoUrl'
import { isRelativeUrl } from '@routes/utils/isRelativeUrl'
import { isTelephoneUrl } from '@routes/utils/isTelephoneUrl'
import { makeUrlWithParams } from '@routes/utils/makeUrlWithParams'
import { logError } from '@util/logger'
import { INLinkProps } from './types'

/**
 * Determines and validates the URL from the `route` and `externalUrl` props
 */
export function getHrefFromProps({
    route: routeProp,
    params,
    externalUrl,
    debug,
}: Pick<INLinkProps, 'route' | 'params' | 'externalUrl' | 'debug'>): string | undefined {
    if (routeProp) {
        if (debug) {
            console.log('<INLink> getHrefFromProps() using the route prop ', routeProp)
        }

        if (routeProp instanceof Route) {
            let route = routeProp

            if (params) {
                route = route.params(params)
            }

            return route.toString()
        }

        // `routeProp` must be a string, so check if it matches on of our known routes
        let route = getRouteByPath(routeProp)

        if (!route) {
            logError(
                `Unknown route string: “${routeProp}”. For non-InterNations URLs, pass a string to 'externalUrl' instead.`
            )

            return
        }

        // We still need to provide `.path` arguments before we can use the route
        if (!route.isReady) {
            const pathArgs = route.tryToExtractArgsFromPathString(routeProp)

            if (pathArgs.length > 0) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Since we don't know which route definition this is, we won't know what the argument types should be, so we have to just hope for the best
                route = route.path(...pathArgs)
            } else {
                logError(
                    `Unable to parse the path arguments from the URL string “${routeProp}” for route with ID “${route.id}”`
                )
            }
        }

        if (params) {
            route = route.params(params)
        }

        return route.toString()
    }

    if (externalUrl) {
        if (debug) {
            console.log('<INLink> getHrefFromProps() using the externalUrl prop ', externalUrl)
        }

        // Make sure `externalUrl` is not being used for an internal route
        if (process.env.STAGE !== 'prod' && process.env.STAGE !== 'stage') {
            if (getRouteByPath(externalUrl)) {
                logError(
                    `External URL “${externalUrl}” is a known route. Pass its route definition to INLink/INButton/INButtonLink instead.`
                )

                return
            }

            // Make sure `externalUrl` is not being used for a relative URL which should be documented with a route definition
            if (isRelativeUrl(externalUrl) && !isMailtoUrl(externalUrl) && !isTelephoneUrl(externalUrl)) {
                logError(
                    `Relative externalUrl “${externalUrl}” does not match with any known routes. For an internal link, document the route and pass it using the 'route' prop. For links outside the of the InterNations community app, pass an absolute URL to 'externalUrl'.`
                )

                return
            }
        }

        if (params) {
            return makeUrlWithParams(externalUrl, params).toString()
        }

        return externalUrl
    }

    return
}
